"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import axios, { isAxiosError } from "axios";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { UIModeToggle } from "@/components/molecules/ui-mode-toggle";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import useUserStore from "@/lib/stores/use-user";

const formSchema = z.object({
  username: z.string().min(2, {
    message: "Username must be at least 2 characters.",
  }),
  password: z.string().min(6, {
    message: "Password must be at least 6 characters.",
  }),
});

export function LoginForm() {
  const router = useRouter();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const { setUser } = useUserStore();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      setIsLoading(true);

      // Simulate API call
      const { data } = await axios.post("/api/login", values);
      toast({
        title: "Login Berhasil!",
        description: data.message,
      });
      
      // Gunakan auth context untuk login
      login(data.user, data.token);
      setUser(data.user);
      
      // Navigate to dashboard after successful login
      router.push("/dashboard");
    } catch (err: unknown) {
      // Type-checking error to ensure it's an instance of Error
      if (isAxiosError(err)) {
        // Mengakses error yang berasal dari Axios
        const message = err.response?.data?.message || "An error occurred.";
        toast({
          title: "Login Gagal!",
          description: message,
        });
      } else if (err instanceof Error) {
        // Menangani error standar
        toast({
          title: "Login Gagal!",
          description: err.message,
        });
      } else {
        // Jika error bukan instance dari Axios atau Error standar
        toast({
          title: "Login Gagal!",
          description: "An unknown error occurred.",
        });
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="grid gap-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Username</FormLabel>
                <FormControl>
                  <Input placeholder="Enter username" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    placeholder="Enter password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            className="w-full bg-quinaPrimary hover:bg-quinaSecondary"
            type="submit"
            disabled={isLoading}
          >
            {isLoading ? "Logging in..." : "Login"}
          </Button>
        </form>
      </Form>
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">
            Display Options
          </span>
        </div>
      </div>
      <div className="flex justify-center">
        <UIModeToggle />
      </div>
    </div>
  );
}
